import { OnInit, OnDestroy, Directive } from '@angular/core'
import { Observable, Subscription, from, of } from 'rxjs'
import { mergeMap, take, map, toArray } from 'rxjs/operators'
import { AuthService } from '@shared/services/auth.service'
import { EventService } from '@shared/services/event.service'
import { AttendeesService } from '@shared/services/attendees.service'
import { AppUser } from '@shared/models/app-user.model'
import { EventModel } from '@shared/models/event.model'
import { PublicPageTitleService } from '@public/app/services/public-page-title.service'
import { LoadingIndicatorService } from '@public/app/services/loading-indicator.service'
import { ToastService } from '@public/app/services/toast.service'
import { callableErrorMessage } from '@shared/errors/callable-error-message'

@Directive()
export abstract class BaseEventListComponent implements OnInit, OnDestroy {
  eventList: EventModel[] = []
  loadState: 'loading' | 'ready' | 'error' = 'loading'
  subscriptions: Subscription[] = []
  pageTitle = ''

  protected constructor(
    protected authService: AuthService,
    protected eventService: EventService,
    protected attendeesService: AttendeesService,
    protected publicPageTitleService: PublicPageTitleService,
    protected loadingIndicatorService: LoadingIndicatorService,
    protected toastService: ToastService
  ) {}

  protected setEventRegistrationStatus(event: EventModel, user: AppUser | null): Observable<EventModel> {
    if (!user) {
      event.isRegistered = false
      return of(event)
    }

    return this.attendeesService.isAlreadyAppliedForEvent(user.id, event.id).pipe(
      take(1),
      map(isApplied => {
        event.isRegistered = isApplied
        return event
      })
    )
  }

  ngOnInit(): void {
    this.publicPageTitleService.setPageTitle(this.pageTitle)
    this.loadEvents()
  }

  reload(): void {
    this.loadEvents()
  }

  private loadEvents(): void {
    this.loadState = 'loading'
    this.loadingIndicatorService.showLoading()
    const sub = from(this.loadFutureApprovedEvents())
      .pipe(
        mergeMap(events => {
          return from(events).pipe(
            mergeMap(event => this.setEventRegistrationStatus(event, this.authService.getCurrentUser())),
            toArray(),
            map(eventsWithRegistration => this.transformEvents(eventsWithRegistration.sort(EventService.byStart)))
          )
        })
      )
      .subscribe({
        next: events => {
          this.eventList = events
          this.loadState = 'ready'
          this.loadingIndicatorService.hideLoading()
        },
        error: error => {
          console.error('Error loading events:', error)
          this.eventList = []
          this.loadState = 'error'
          this.loadingIndicatorService.hideLoading()
        },
      })

    this.subscriptions.push(sub)
  }

  private async loadFutureApprovedEvents(): Promise<EventModel[]> {
    const events = await this.eventService.fetchAllByDateRange({ from: Date.now() - EventService.UPCOMING_GRACE_MS })
    return events.filter(event => this.isFutureAndApproved(event))
  }

  private isFutureAndApproved(event: EventModel): boolean {
    return EventService.isUpcoming(event) && (!!event.isApproved || event.isApproved === undefined)
  }

  onUnregister(event: EventModel): void {
    this.loadingIndicatorService.showLoading()
    if (!this.authService.getCurrentUser()) {
      // console.log('Missing feature: handle no user')
      this.loadingIndicatorService.hideLoading()
      return
    }
    this.attendeesService
      .cancelApplicationToEvent(this.authService.getCurrentUser().id, event.id, event.organiserId)
      .subscribe({
        next: result => {
          this.updateEventListAfterUnregister({
            ...event,
            isRegistered: false,
            numberOfAttendees: result?.numberOfAttendees ?? event.numberOfAttendees - 1,
          })
          this.toastService.showSuccess('Eseményről való lejelentkezés sikeres')
        },
        error: err => {
          const errorMsg = callableErrorMessage(err)
          this.loadingIndicatorService.hideLoading()
          this.toastService.showError(errorMsg)
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading()
        },
      })
  }

  protected transformEvents(events: EventModel[]): EventModel[] {
    return events
  }

  protected updateEventListAfterUnregister(event: EventModel): void {
    // console.log('Missing feature: handle update of event list after unregister in child class if needed')
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }
}
